import { Alert, Button, Stack } from '@mui/material';
import { useBulkAnnotationWorkingState } from 'components/StudyDashboard/Tasks/useBulkAnnotationWorkingState';
import { AnnotationAssignment, AnnotationWorkingStates } from 'interfaces/annotation';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { usePermissions } from 'utils/usePermissions';

import {
  getAnalysisAreasDatasetUrl,
  getUndoneIncludeExcludeAnnotations,
  undoneIncludeExcludeWarning,
} from './analysisAreas';

interface AnalysisAreasReadinessNoticeProps {
  assignment: AnnotationAssignment;
  studyId: string;
  labId?: string;
  doneLabel: string;
}

export const AnalysisAreasReadinessNotice: React.FC<AnalysisAreasReadinessNoticeProps> = ({
  assignment,
  studyId,
  labId,
  doneLabel,
}) => {
  const undoneAnnotations = getUndoneIncludeExcludeAnnotations(assignment);
  const { hasPermission } = usePermissions();
  const canMarkDone = hasPermission(Permission.AnnotateSlides);
  const { submit, pendingTarget } = useBulkAnnotationWorkingState({
    annotationAssignmentId: assignment.annotationAssignmentId,
    selectedRows: undoneAnnotations,
    onCompleted: () => undefined,
  });

  if (!undoneAnnotations.length) {
    return null;
  }

  const reviewUrl = getAnalysisAreasDatasetUrl({
    studyId,
    labId,
    slideIds: undoneAnnotations.map((annotation) => annotation.slideId),
    annotationAssignmentId: assignment.annotationAssignmentId,
  });

  return (
    <Alert severity="warning" sx={{ alignItems: 'flex-start', maxWidth: 420 }}>
      <Stack spacing={1} alignItems="flex-start">
        {undoneIncludeExcludeWarning(undoneAnnotations)}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canMarkDone ? (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              disabled={pendingTarget != null}
              onClick={() => submit(AnnotationWorkingStates.Done)}
            >
              {pendingTarget === AnnotationWorkingStates.Done ? 'Marking as done' : doneLabel}
            </Button>
          ) : null}
          <Button size="small" variant="outlined" color="secondary" component={RouterLink} to={reviewUrl}>
            Review
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
};
